import { motion } from "framer-motion";
import { ArrowRight, MapPin, Flame } from "lucide-react";
import { IMAGES, WHATSAPP_URL } from "../../lib/constants";

const particles = Array.from({ length: 18 });

export default function Hero() {
    return (
        <section
            id="inicio"
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505] flex items-end pt-28 pb-16 md:pb-24"
        >
            
            {/* Background image */}
            <div className="absolute inset-0 z-0">
                <img
                    src={IMAGES.womanLift1}
                    alt="Mujer entrenando con pesas"
                    className="w-full h-full object-cover object-[center_25%] opacity-70"
                />
                <div className="absolute inset-0 hero-overlay" />
            </div>

            {/* Smoke / radial */}
            <div className="absolute -left-20 top-1/3 w-[600px] h-[600px] rounded-full bg-brand-orange/20 blur-[140px] smoke z-0" />
            <div className="absolute right-0 bottom-0 w-[500px] h-[500px] rounded-full bg-brand-gold/10 blur-[160px] smoke z-0" />

            {/* Particles */}
            {particles.map((_, i) => (
                <span
                    key={i}
                    className="particle z-10"
                    style={{
                        width: `${4 + Math.random() * 10}px`,
                        height: `${4 + Math.random() * 10}px`,
                        left: `${Math.random() * 100}%`,
                        top: `${20 + Math.random() * 70}%`,
                        animationDelay: `${Math.random() * 6}s`,
                        animationDuration: `${5 + Math.random() * 6}s`,
                    }}
                />
            ))}

            {/* Side label */}
           <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 origin-left -rotate-90 items-center gap-3">
    <span className="h-px w-12 bg-brand-orange"></span>
    <span className="font-display tracking-[0.4em] text-xs text-brand-muted" translate="no">
        F YOU - EST. MADRID
    </span>
</div>

            {/* Stats bar right */}
            <div className="hidden xl:block absolute right-10 top-32 z-20 text-right">
                <div className="font-display text-6xl text-brand-orange leading-none">
                    +500
                </div>
                <div className="font-display tracking-widest text-xs text-brand-muted mt-2">
                    ALUMNOS TRANSFORMADOS
                </div>
                <div className="h-px w-20 bg-brand-gold ml-auto my-6" />
                <div className="font-display text-6xl gold-text leading-none">
                    3
                </div>
                <div className="font-display tracking-widest text-xs text-brand-muted mt-2">
                    CENTROS EN MADRID
                </div>
            </div>

            <div className="relative z-20 max-w-7xl mx-auto px-5 md:px-10 w-full">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="flex items-center gap-3 mb-6"
                >
                    <Flame className="text-brand-orange" size={18} />
                    <span className="font-display tracking-[0.4em] text-xs md:text-sm text-brand-gold">
                        ENTRENAMIENTO PERSONAL DE ÉLITE
                    </span>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 }}
                    data-testid="hero-headline"
                    className="font-display text-[14vw] sm:text-7xl md:text-8xl lg:text-9xl xl:text-[10rem] leading-[0.85] tracking-tighter text-brand-text"
                >
                    CONSTRUYE LA
                    <br />
                    <span className="orange-text">MEJOR VERSIÓN</span>
                    <br />
                    DE TI.
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mt-7 max-w-xl text-base md:text-lg text-brand-muted leading-relaxed"
                >
                    Entrenamiento personal de élite para personas que quieren
                    <span className="text-brand-text font-medium">
                        {" "}resultados reales
                    </span>. Sin atajos. Sin excusas. Solo disciplina.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-cta-empieza"
                        className="group inline-flex items-center justify-center gap-3 bg-brand-orange hover:bg-brand-orangeHover text-black font-display tracking-wider text-base px-8 py-5 transition-all duration-300 hover:scale-[1.02]"
                    >
                        EMPIEZA HOY
                        <ArrowRight
                            size={18}
                            className="group-hover:translate-x-1 transition-transform"
                        />
                    </a>
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-cta-valoracion"
                        className="inline-flex items-center justify-center gap-3 border border-brand-gold/70 hover:bg-brand-gold hover:text-black text-brand-gold font-display tracking-wider text-base px-8 py-5 transition-all duration-300"
                    >
                        RESERVA VALORACIÓN
                    </a>
                    <a
                        href="#centros"
                        data-testid="hero-cta-centros"
                        className="inline-flex items-center justify-center gap-2 text-brand-text hover:text-brand-orange font-display tracking-wider text-base px-4 py-5 transition-colors"
                    >
                        <MapPin size={18} /> VER CENTROS
                    </a>
                </motion.div>

                {/* Bottom ticker */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.9 }}
                    className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs font-display tracking-[0.3em] text-brand-muted"
                >
                    <span className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-brand-orange pulse-dot" />
                        PARLA
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-brand-orange pulse-dot" />
                        GETAFE
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-brand-orange pulse-dot" />
                        FUENLABRADA
                    </span>
                    <span className="text-brand-gold">
                        DENTRO DE BASIC-FIT • PROGRAMA EXCLUSIVO F YOU
                    </span>
                </motion.div>
            </div>
        </section>
    );
}
